<template>
  <el-form
    ref="formRef"
    :model="form"
    :rules="rules"
    label-position="top"
    class="ag-form"
  >
    <el-form-item label="Título" prop="title" required>
      <el-input v-model="form.title" placeholder="" />
    </el-form-item>

    <el-form-item label="Descrição" prop="description">
      <el-input v-model="form.description" type="textarea" />
    </el-form-item>

    <el-form-item label="Cidades" prop="cities" required>
      <el-checkbox-group v-model="form.cities">
        <el-checkbox value="sp" name="cities"> São Paulo </el-checkbox>
        <el-checkbox value="rj" name="cities"> Rio de Janeiro </el-checkbox>
      </el-checkbox-group>
    </el-form-item>

    <el-form-item v-if="canChooseBusinessClient" label="Empresa" prop="selectedCompany" required>
      <el-select
        v-model="form.selectedCompany"
        placeholder="Selecione a empresa"
        filterable
        :loading="loadingCompanies"
      >
        <el-option
          v-for="company in availableCompanies"
          :key="company"
          :label="company"
          :value="company"
        />
      </el-select>
    </el-form-item>

    <el-form-item label="Data e Hora" prop="startDateTime" required>
      <VDatePicker
        v-model="form.startDateTime"
        mode="dateTime"
        is24hr
        :locale="'pt-BR'"
        :minute-increment="5"
        :min-date="minDate"
        :rules="timeRules"
        :disabled-dates="disabledDates"
        class="vc-picker"
      >
        <template #default="{ togglePopover, inputValue, inputEvents }">
          <div class="vc-input-wrap">
            <button
              type="button"
              class="vc-input-trigger"
              aria-label="Abrir seletor de data e hora"
              @click="togglePopover"
            >
              <el-icon class="vc-input-icon"><Calendar /></el-icon>
            </button>
            <input
              class="vc-input"
              :value="inputValue"
              v-on="inputEvents"
              inputmode="numeric"
              placeholder="Digite ou selecione a data e hora inicial"
            />
          </div>
        </template>
      </VDatePicker>
    </el-form-item>

    <el-form-item label="Criptografia">
      <el-switch v-model="form.encryptionEnabled" :disabled="submitting" aria-label="Ativar criptografia" active-text="Ativar criptografia" />
    </el-form-item>
    <el-form-item v-if="form.encryptionEnabled" label="Chave de criptografia" prop="encryptionKeyId" required>
      <div class="encryption-key-row">
        <el-select v-model="form.encryptionKeyId" :loading="loadingKeys" :disabled="submitting || !effectiveBusinessClient" placeholder="Selecione a chave do cliente">
          <el-option v-for="key in encryptionKeys" :key="key.id" :value="key.id" :label="`${key.name} (v${key.version})`" />
        </el-select>
        <el-tooltip content="Atualizar chaves" placement="top">
          <el-button
            class="refresh-keys-button"
            :loading="loadingKeys"
            :disabled="submitting || !effectiveBusinessClient"
            aria-label="Atualizar chaves"
            @click="fetchEncryptionKeys"
          >
            <el-icon v-if="!loadingKeys"><Refresh /></el-icon>
          </el-button>
        </el-tooltip>
      </div>
      <div class="upload-hint">O cliente é responsável por descriptografar os arquivos com sua chave privada.</div>
      <el-alert v-if="keysError" :title="keysError" type="error" :closable="false" />
      <div v-else-if="!loadingKeys && !encryptionKeys.length" class="upload-hint">
        {{ effectiveBusinessClient ? 'Nenhuma chave cadastrada para este cliente.' : 'Selecione a empresa primeiro.' }}
        <router-link to="/settings?tab=encryption-keys">Gerenciar chaves nas configurações</router-link>
      </div>
    </el-form-item>

    <el-form-item label="Arquivos" prop="files">
      <el-upload
        ref="uploader"
        class="upload-block"
        drag
        multiple
        :auto-upload="false"
        :file-list="form.files"
        @change="handleFileChange"
        @remove="handleFileChange"
      >
        <el-icon class="upload-icon"><UploadFilled /></el-icon>
        <div class="el-upload__text">
          Arraste o arquivo aqui ou <em>clique para selecionar</em>
        </div>
      </el-upload>
      <div class="upload-hint">Voce pode anexar mais de um arquivo.</div>
    </el-form-item>

    <el-form-item v-if="submitting" class="upload-progress-item">
      <div class="upload-progress-wrap">
        <el-progress
          :percentage="uploadProgressPercent"
          :striped="true"
          :striped-flow="true"
          :duration="10"
          status=""
        />
        <span class="upload-progress-text">{{ uploadProgressText }}</span>
      </div>
    </el-form-item>

    <el-form-item>
      <el-button
        class="btn-plain"
        plain
        :loading="submitting"
        :disabled="submitting"
        @click="onSubmit"
      >
        Agendar
      </el-button>
    </el-form-item>
  </el-form>
</template>

<script>
import axios from "axios";
import { ElMessage } from "element-plus";
import { UploadFilled, Calendar, Refresh } from "@element-plus/icons-vue";

import { config } from "../../config";
import { loadBusinessClients } from "../../services/businessClients";
import { listEncryptionKeys, encryptionError } from "../../services/encryptionKeys";

const FRM_CLIENT = "frm";
const WEDNESDAY = 3;
const FRM_ALLOWED_HOUR = 9;
const FRM_ALLOWED_MINUTE = 0;
const FRM_DISABLED_WEEKDAYS = [1, 2, 3, 5, 6, 7];

function normalizeBusinessClientName(value) {
  return String(value || "").trim().toLowerCase();
}

function isFrmBusinessClient(businessClient) {
  return normalizeBusinessClientName(businessClient) === FRM_CLIENT;
}

function isSameCalendarDate(leftDate, rightDate) {
  return leftDate.getFullYear() === rightDate.getFullYear()
    && leftDate.getMonth() === rightDate.getMonth()
    && leftDate.getDate() === rightDate.getDate();
}

function isFrmRestrictedSchedule(dateTime, businessClient) {
  if (!isFrmBusinessClient(businessClient)) {
    return true;
  }

  const parsedDate = new Date(dateTime);
  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  const weekday = parsedDate.getDay();
  const hour = parsedDate.getHours();
  const minute = parsedDate.getMinutes();

  return weekday === WEDNESDAY && hour === FRM_ALLOWED_HOUR && minute === FRM_ALLOWED_MINUTE;
}

function getFrmScheduleErrorMessage() {
  return "Agendamentos para FRM sao permitidos somente as quartas-feiras, as 09:00.";
}

export default {
  name: "ScheduleForm",
  components: { UploadFilled, Calendar, Refresh },
  emits: ["submit"],
  props: {
    businessClients: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        title: "",
        description: "",
        cities: [],
        startDateTime: null,
        files: [],
        selectedCompany: "",
        encryptionEnabled: false,
        encryptionKeyId: "",
      },
      businessClient: null,
      canChooseBusinessClient: false,
      loadingCompanies: false,
      encryptionKeys: [],
      loadingKeys: false,
      keysError: "",
      keyRequestId: 0,
      submitting: false,
      uploadProgress: { loaded: 0, total: 0 },
      availableCompanies: [],
      rules: {
        encryptionKeyId: [{ validator: (_rule, value, callback) => {
          if (this.form.encryptionEnabled && !this.encryptionKeys.some((key) => key.id === value)) {
            callback(new Error("Selecione uma chave válida para este cliente"));
          } else callback();
        }, trigger: "change" }],
        title: [
          { required: true, message: "Informe o título", trigger: "blur" },
        ],
        cities: [
          {
            required: true,
            message: "Selecione as cidades",
            trigger: "change",
          },
        ],
        startDateTime: [
          {
            required: true,
            message: "Informe data e hora inicial",
            trigger: "change",
          },
        ],
        selectedCompany: [
          {
            required: true,
            message: "Selecione a empresa",
            trigger: "change",
          },
        ],
      },
    };
  },
  computed: {
    effectiveBusinessClient() {
      return this.canChooseBusinessClient
        ? this.form.selectedCompany
        : this.businessClient;
    },
    minDate() {
      const now = new Date();
      now.setSeconds(0, 0);
      return now;
    },
    timeRules() {
      const selectedDate = this.form.startDateTime ? new Date(this.form.startDateTime) : null;
      const now = new Date();
      const isTodaySelection = selectedDate && !Number.isNaN(selectedDate.getTime())
        ? isSameCalendarDate(selectedDate, now)
        : false;
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const isFrmClient = isFrmBusinessClient(this.effectiveBusinessClient);

      if (!isFrmClient && !isTodaySelection) {
        return "auto";
      }

      return {
        hours: (hour) => {
          if (isFrmClient && hour !== FRM_ALLOWED_HOUR) {
            return false;
          }

          if (isTodaySelection && hour < currentHour) {
            return false;
          }

          return true;
        },
        minutes: (minute, parts) => {
          if (isFrmClient) {
            return parts.hours === FRM_ALLOWED_HOUR && minute === FRM_ALLOWED_MINUTE;
          }

          if (isTodaySelection && parts.hours === currentHour) {
            return minute > currentMinute;
          }

          return true;
        },
      };
    },
    uploadProgressPercent() {
      if (!this.uploadProgress.total) return 0;
      return Math.min(Math.round((this.uploadProgress.loaded / this.uploadProgress.total) * 100), 100);
    },
    uploadProgressText() {
      const fmt = (bytes) => {
        if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${bytes} B`;
      };
      return `${fmt(this.uploadProgress.loaded)} / ${fmt(this.uploadProgress.total || 0)}`;
    },
    disabledDates() {
      if (!isFrmBusinessClient(this.effectiveBusinessClient)) {
        return [];
      }

      return [
        {
          repeat: {
            weekdays: FRM_DISABLED_WEEKDAYS,
          },
        },
      ];
    },
  },
  watch: {
    effectiveBusinessClient() {
      this.form.encryptionKeyId = "";
      this.encryptionKeys = [];
      if (this.form.encryptionEnabled) this.fetchEncryptionKeys();
    },
    "form.encryptionEnabled"(enabled) {
      this.form.encryptionKeyId = "";
      if (enabled) this.fetchEncryptionKeys();
    },
    businessClients: {
      deep: true,
      handler(clients) {
        this.applyBusinessClients(clients);
      },
    },
  },
  async created() {
    this.applyBusinessClients(this.businessClients);
    if (!this.availableCompanies.length) {
      await this.fetchBusinessClients();
      this.applyBusinessClients(this.availableCompanies);
    }
  },
  methods: {
    async fetchEncryptionKeys() {
      const requestId = ++this.keyRequestId;
      const client = this.effectiveBusinessClient;
      this.encryptionKeys = [];
      this.form.encryptionKeyId = "";
      this.keysError = "";
      this.loadingKeys = false;
      if (!client) return;
      this.loadingKeys = true;
      try {
        const keys = await listEncryptionKeys(client);
        if (requestId === this.keyRequestId && client === this.effectiveBusinessClient) this.encryptionKeys = keys;
      } catch (error) {
        if (requestId === this.keyRequestId) this.keysError = encryptionError(error);
      } finally {
        if (requestId === this.keyRequestId) this.loadingKeys = false;
      }
    },
    normalizeBusinessClients(payload) {
      const raw = Array.isArray(payload)
        ? payload
        : payload?.business_clients || payload?.data || payload?.results || [];

      const names = raw
        .map((item) => {
          if (typeof item === "string") return item;
          if (typeof item === "object") {
            return item.business_client || item.name || item.client || "";
          }
          return "";
        })
        .map((name) => String(name || "").trim().toLowerCase())
        .filter(Boolean);

      return [...new Set(names)].sort((a, b) => a.localeCompare(b));
    },

    applyBusinessClients(clients) {
      this.availableCompanies = this.normalizeBusinessClients(clients);
      this.canChooseBusinessClient = this.availableCompanies.length > 1;
      this.businessClient = this.availableCompanies.length === 1
        ? this.availableCompanies[0]
        : null;

      if (this.businessClient) {
        this.form.selectedCompany = this.businessClient;
      } else if (!this.availableCompanies.includes(this.form.selectedCompany)) {
        this.form.selectedCompany = "";
      }
    },

    async fetchBusinessClients() {
      this.loadingCompanies = true;
      try {
        this.availableCompanies = await loadBusinessClients();
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
        ElMessage.error("Nao foi possivel carregar as empresas.");
      } finally {
        this.loadingCompanies = false;
      }
    },

    sanitizeFileName(fileName) {
      const safeName = String(fileName || "arquivo").trim();
      const lastDot = safeName.lastIndexOf(".");
      const hasExt = lastDot > 0;
      const base = hasExt ? safeName.slice(0, lastDot) : safeName;
      const ext = hasExt ? safeName.slice(lastDot + 1) : "";

      const normalizedBase = base
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase();

      const normalizedExt = ext
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "")
        .toLowerCase();

      const finalBase = normalizedBase || "arquivo";
      return normalizedExt ? `${finalBase}.${normalizedExt}` : finalBase;
    },

    createSanitizedFile(file) {
      const sanitizedName = this.sanitizeFileName(file?.name);
      if (sanitizedName === file?.name) return file;

      return new File([file], sanitizedName, {
        type: file?.type || "application/octet-stream",
        lastModified: file?.lastModified || Date.now(),
      });
    },

    sanitizeUploadFile(uploadFile) {
      const rawFile = uploadFile?.raw || uploadFile;
      if (!rawFile) return uploadFile;

      const sanitizedRaw = this.createSanitizedFile(rawFile);
      return {
        ...uploadFile,
        name: sanitizedRaw.name,
        raw: sanitizedRaw,
      };
    },

    buildStartDate(dateTime) {
      const parsedDate = new Date(dateTime);

      if (Number.isNaN(parsedDate.getTime())) {
        throw new Error("Data e hora inicial invalida.");
      }

      if (parsedDate.getTime() <= Date.now()) {
        throw new Error("A data e hora inicial deve ser futura.");
      }

      return parsedDate.toISOString();
    },

    validateSchedulingWindow(businessClient, dateTime) {
      if (!isFrmRestrictedSchedule(dateTime, businessClient)) {
        throw new Error(getFrmScheduleErrorMessage());
      }
    },

    handleFileChange(file, fileList) {
      this.form.files = (fileList || []).map((item) => this.sanitizeUploadFile(item));
      this.$nextTick(() => {
        this.syncUploadNameTooltips();
      });
    },

    syncUploadNameTooltips() {
      const uploadRoot = this.$refs.uploader?.$el;
      if (!uploadRoot) return;

      const fileNameEls = uploadRoot.querySelectorAll(".el-upload-list__item-name");
      fileNameEls.forEach((el) => {
        const fullName = (el.textContent || "").trim();
        if (!fullName) return;
        el.setAttribute("title", fullName);
      });
    },

    async onSubmit() {
      if (this.submitting) return;

      this.$refs.formRef.validate(async (ok) => {
        if (!ok || this.submitting) return;

        const businessClient = this.effectiveBusinessClient;

        if (!businessClient) {
          ElMessage.error("Nao foi possivel identificar a empresa para o agendamento.");
          return;
        }

        let startDate;

        try {
          this.validateSchedulingWindow(businessClient, this.form.startDateTime);
          startDate = this.buildStartDate(this.form.startDateTime);
        } catch (error) {
          ElMessage.error(error.message);
          return;
        }

        const formData = new FormData();
        const schedulePayload = {
          title: this.form.title,
          cities: this.form.cities,
          start_date: startDate,
          request_origin: "web",
          encryption_enabled: this.form.encryptionEnabled,
          encryption_key_id: this.form.encryptionEnabled ? this.form.encryptionKeyId : null,
        };
        if (this.form.description) schedulePayload.description = this.form.description;

        formData.append("data", JSON.stringify(schedulePayload));
        formData.append("business_client", businessClient || "");
        this.form.files.forEach((f) => {
          const rawFile = f.raw || f;
          const sanitizedFile = this.createSanitizedFile(rawFile);
          formData.append("files", sanitizedFile);
        });

        this.uploadProgress = { loaded: 0, total: 0 };
        this.submitting = true;

        axios
          .post(`${config.API}/v1/schedule/`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (event) => {
              this.uploadProgress.loaded = event.loaded;
              this.uploadProgress.total = event.total || 0;
            },
          })
          .then(() => {
            this.$emit("submit");
            ElMessage.success("Agendamento concluído");
          })
          .catch((error) => {
            console.error("Erro ao enviar os dados:", error);
            ElMessage.error(encryptionError(error));
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/fonts.css";

.ag-form {
  display: grid;
  row-gap: 16px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  font-family: "Globo-Regular", sans-serif;
}

.ag-form :deep(.el-form-item) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-bottom: 0;
}

.ag-form :deep(.el-form-item__content) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.ag-form :deep(.el-form-item__content > *) {
  max-width: 100%;
  min-width: 0;
}

.ag-form :deep(.el-form-item__label) {
  color: #2b4054;
  font-family: "Globo-Regular", sans-serif;
}

.ag-form :deep(.is-required .el-form-item__label)::before {
  color: #e15757;
}

.ag-form :deep(.el-input__wrapper) {
  min-height: 40px;
}

.ag-form :deep(.el-textarea__inner) {
  min-height: 80px;
}

.ag-form :deep(.el-radio-group) {
  column-gap: 14px;
}

.ag-form :deep(.el-date-editor--daterange),
.ag-form :deep(.el-time-editor),
.ag-form :deep(.el-input),
.ag-form :deep(.el-textarea) {
  width: 100%;
}

.ag-form :deep(.el-range-separator) {
  padding: 0 8px;
  color: #2b4054;
}

.btn-plain {
  color: #3a6d8c;
  border-color: #dfe5eb;
  font-weight: 600;
  display: block;
  margin: 8px auto 0;
}

.ag-form :deep(.el-form-item:last-child .el-form-item__content) {
  justify-content: center;
}

.upload-icon {
  font-size: 40px;
  margin-bottom: 8px;
  color: #3a6d8c;
}

.upload-block,
.upload-block :deep(.el-upload),
.upload-block :deep(.el-upload-dragger) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.upload-block :deep(.el-upload-dragger) {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
  text-align: center;
}

.upload-block :deep(.el-upload__text) {
  width: 100%;
  max-width: 100%;
  margin: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
}

.upload-block :deep(.el-upload-list) {
  max-width: 100%;
  overflow-x: hidden;
}

.upload-block :deep(.el-upload-list__item) {
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.upload-block :deep(.el-upload-list__item-info) {
  min-width: 0;
  max-width: 100%;
}

.upload-block :deep(.el-upload-list__item-name) {
  display: block;
  width: min(100%, 50ch);
  max-width: 100%;
  min-width: 0;
  margin-right: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.encryption-key-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 40px;
  gap: 8px;
  width: 100%;
}

.refresh-keys-button {
  width: 40px;
  min-height: 40px;
  padding: 0;
  color: #3a6d8c;
  border-color: #dfe5eb;
  background: #fff;
}

.refresh-keys-button:hover,
.refresh-keys-button:focus {
  color: #fff;
  border-color: #3a6d8c;
  background: #3a6d8c;
}

.upload-hint {
  margin-top: 8px;
  color: #6f7b87;
  font-size: 12px;
  font-family: "Globo-Regular", sans-serif;
}

.upload-progress-item {
  margin-bottom: 0 !important;
}

.upload-progress-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.upload-progress-text {
  font-size: 12px;
  color: #6f7b87;
  font-family: "Globo-Regular", sans-serif;
  text-align: center;
}

.vc-picker {
  width: 100%;
  position: relative;
}

.vc-picker :deep(.vc-popover-content-wrapper) {
  left: 50% !important;
  transform: translateX(-50%) !important;
  top: calc(100% + 4px) !important;
}

.vc-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0 12px 0 0;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.vc-input-wrap:hover,
.vc-input-wrap:focus-within {
  border-color: #3a6d8c;
}

.vc-input-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.vc-input-icon {
  color: #9aa4af;
  flex-shrink: 0;
}

.vc-input {
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 14px;
  font-family: "Globo-Regular", sans-serif;
  color: #2b4054;
  cursor: text;
}

@media (max-width: 640px) {
  .ag-form {
    row-gap: 12px;
  }

  .encryption-key-row {
    grid-template-columns: minmax(0, 1fr) 36px;
  }

  .refresh-keys-button {
    width: 36px;
  }

  .upload-block :deep(.el-upload-dragger) {
    min-height: 130px;
    padding: 12px;
  }
}
</style>
